/**
 * MergeFieldMapper Component
 * Maps SendGrid template merge fields to contact/voucher/content data sources.
 * Also allows adding custom variables not yet in the template.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CONTACT_FIELDS,
  VOUCHER_FIELDS,
  SYSTEM_FIELDS,
  type FieldMapping,
  suggestFieldMapping,
  buildStaticLookupBundleMappings,
  isSendGridReservedField,
  getReservedFieldInfo,
} from "../../services/templates.service";
import LookupSourceEditor from "./LookupSourceEditor";
import ContentSourceEditor from "./ContentSourceEditor";
import { getLookupRows, getLookups } from "../../services/lookups.service";

interface MergeFieldMapperProps {
  mergeFields: string[];
  mappings: FieldMapping[];
  onChange: (mappings: FieldMapping[]) => void;
  includeVoucherFields?: boolean;
  contactFields?: ReadonlyArray<{ value: string; label: string }>;
  // When provided, the Source dropdowns are filtered to this set.
  // Used by surfaces that don't have all sourceTypes available (e.g.,
  // form-handler autoresponder, which doesn't have a voucher/landing-page
  // context). Defaults to all sourceTypes.
  allowedSourceTypes?: ReadonlyArray<FieldMapping["sourceType"]>;
}

const ALL_SOURCE_TYPE_OPTIONS: ReadonlyArray<{
  value: FieldMapping["sourceType"];
  label: string;
}> = [
  // Direct content first — the default newsletter path (fill the slot directly).
  { value: "custom", label: "Custom value (text / HTML)" },
  // v1: IMAGE-only Content Items resolved to public storage URLs at
  // send time. See ContentSourceEditor + supabase/_shared/
  // flightSendHelpers.ts:loadContentUrls for the resolution + the
  // hard-validation behavior (no silent blanks).
  { value: "content", label: "Content item (image)" },
  // Per-recipient personalization.
  { value: "contact", label: "Contact field" },
  { value: "voucher", label: "Voucher field" },
  { value: "url", label: "Link / URL" },
  { value: "landing_page", label: "Landing page link" },
  // Advanced — vary content per recipient within one mixed-audience send.
  {
    value: "lookup",
    label: "Lookup table (static bundle or per-recipient)",
  },
];

export default function MergeFieldMapper({
  mergeFields,
  mappings,
  onChange,
  includeVoucherFields = true,
  contactFields = CONTACT_FIELDS,
  allowedSourceTypes,
}: MergeFieldMapperProps) {
  // Visible source-type options after applying:
  //   - allowedSourceTypes filter (caller-provided subset)
  //   - includeVoucherFields filter (legacy prop; suppresses 'voucher')
  const visibleSourceTypes = ALL_SOURCE_TYPE_OPTIONS.filter((opt) => {
    if (allowedSourceTypes && !allowedSourceTypes.includes(opt.value)) {
      return false;
    }
    if (opt.value === "voucher" && !includeVoucherFields) {
      return false;
    }
    return true;
  });
  const [newVarName, setNewVarName] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [bundleLookupId, setBundleLookupId] = useState("");
  const canUseLookups = visibleSourceTypes.some((o) => o.value === "lookup");
  const lookupsQ = useQuery({
    queryKey: ["campaign-lookups"],
    queryFn: getLookups,
    enabled: canUseLookups && mergeFields.length > 0,
  });
  const bundleRowsQ = useQuery({
    queryKey: ["campaign-lookup-rows", bundleLookupId],
    queryFn: () => getLookupRows(bundleLookupId),
    enabled: canUseLookups && !!bundleLookupId,
  });
  const bundleLookup = (lookupsQ.data ?? []).find(
    (l) => l.id === bundleLookupId,
  );
  const bundleMappingResult = useMemo(
    () =>
      bundleLookup
        ? buildStaticLookupBundleMappings({
            mergeFields,
            mappings,
            lookupId: bundleLookup.id,
            keyColumn: bundleLookup.key_column,
            columnNames: bundleLookup.column_schema.map((c) => c.name),
          })
        : { mappings: [...mappings], matchedFields: [] },
    [bundleLookup, mergeFields, mappings],
  );
  const bundleMatches = bundleMappingResult.matchedFields;
  const bundleHasDefault = (bundleRowsQ.data ?? []).some(
    (row) => row.is_default,
  );

  // Variables the user added manually (not auto-detected from template)
  const extraMappings = mappings.filter(
    (m) =>
      !mergeFields.includes(m.templateField) && m.sourceType !== "sendgrid",
  );

  const handleAddCustomVariable = () => {
    const name = newVarName.trim().replace(/[^a-zA-Z0-9_]/g, "_");
    if (!name) {
      return;
    }
    const alreadyExists =
      mergeFields.includes(name) ||
      mappings.some((m) => m.templateField === name);
    if (alreadyExists) {
      setNewVarName("");
      setShowAddForm(false);
      return;
    }
    onChange([
      ...mappings,
      { templateField: name, sourceType: "custom", customValue: "" },
    ]);
    setNewVarName("");
    setShowAddForm(false);
  };

  const handleRemoveExtra = (templateField: string) => {
    onChange(mappings.filter((m) => m.templateField !== templateField));
  };

  const updateMapping = (
    templateField: string,
    updates: Partial<FieldMapping>,
  ) => {
    const existingIndex = mappings.findIndex(
      (m) => m.templateField === templateField,
    );
    const newMappings = [...mappings];

    if (existingIndex >= 0) {
      newMappings[existingIndex] = {
        ...newMappings[existingIndex],
        ...updates,
      };
    } else {
      newMappings.push({
        templateField,
        sourceType: "custom",
        customValue: "",
        ...updates,
      } as FieldMapping);
    }

    onChange(newMappings);
  };

  const getMapping = (templateField: string): FieldMapping => {
    const existing = mappings.find((m) => m.templateField === templateField);
    if (existing) {
      return existing;
    }

    // Try to suggest a mapping
    const suggestion = suggestFieldMapping(templateField);
    return (
      suggestion || {
        templateField,
        sourceType: "custom",
        customValue: "",
      }
    );
  };

  const handleSourceTypeChange = (
    templateField: string,
    sourceType: FieldMapping["sourceType"],
  ) => {
    // Non-destructive switch (newsletter MVP audit constraint): keep the other
    // source types' saved config so reverting a slot between custom / content /
    // lookup doesn't wipe it — existing lookup-based flights stay editable.
    // Safe because BOTH resolvers (templates.service.ts:614 and
    // flightSendHelpers.ts:2092) dispatch strictly on sourceType, so inactive
    // fields are never read at send time and can't render unexpectedly.
    updateMapping(templateField, { sourceType });
  };

  const handleSourceFieldChange = (
    templateField: string,
    sourceField: string,
  ) => {
    updateMapping(templateField, { sourceField });
  };

  const handleCustomValueChange = (
    templateField: string,
    customValue: string,
  ) => {
    updateMapping(templateField, { customValue });
  };

  const autoMapAllFields = () => {
    const newMappings: FieldMapping[] = mergeFields.map((field) => {
      const suggestion = suggestFieldMapping(field);
      return (
        suggestion || {
          templateField: field,
          sourceType: "custom",
          customValue: "",
        }
      );
    });
    onChange(newMappings);
  };

  const clearAllMappings = () => {
    const newMappings: FieldMapping[] = mergeFields.map((field) => {
      // Preserve reserved field mappings
      if (isSendGridReservedField(field)) {
        return { templateField: field, sourceType: "sendgrid" as const };
      }
      return {
        templateField: field,
        sourceType: "custom" as const,
        customValue: "",
      };
    });
    onChange(newMappings);
  };

  const applyStaticContentBundle = () => {
    if (!bundleLookup || !bundleHasDefault || bundleMatches.length === 0) {
      return;
    }
    onChange(bundleMappingResult.mappings);
  };

  return (
    <div className="space-y-4">
      {/* Header with actions — suppressed when no template-derived
          merge fields exist (e.g., form-handler autoresponder use). */}
      {mergeFields.length > 0 && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">
            Map Template Fields ({mergeFields.length})
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={autoMapAllFields}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Auto-map
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={clearAllMappings}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Newsletter/static-content fast path. One marked default row can
          supply every same-named template field without recipient keys. */}
      {mergeFields.length > 0 && canUseLookups && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="text-sm font-medium text-blue-950">
            Map a static content bundle
          </div>
          <p className="mt-1 text-xs text-blue-800">
            Choose a lookup with one starred default row. Fields whose names
            match lookup columns are mapped together and use that row for every
            recipient.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <select
              value={bundleLookupId}
              onChange={(e) => setBundleLookupId(e.target.value)}
              className="min-w-0 flex-1 rounded border border-blue-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">Choose lookup…</option>
              {(lookupsQ.data ?? []).map((lookup) => (
                <option key={lookup.id} value={lookup.id}>
                  {lookup.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={applyStaticContentBundle}
              disabled={
                !bundleLookup ||
                bundleRowsQ.isLoading ||
                !bundleHasDefault ||
                bundleMatches.length === 0
              }
              className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Map {bundleMatches.length || "matching"} fields
            </button>
          </div>
          {bundleLookup && !bundleRowsQ.isLoading && !bundleHasDefault && (
            <p className="mt-2 text-xs font-medium text-red-700">
              This lookup has no starred default row. Open the lookup and mark
              the content row first.
            </p>
          )}
          {bundleLookup && bundleHasDefault && bundleMatches.length === 0 && (
            <p className="mt-2 text-xs font-medium text-amber-800">
              No template field names match this lookup's columns.
            </p>
          )}
          {bundleLookup && bundleHasDefault && bundleMatches.length > 0 && (
            <p className="mt-2 text-xs text-blue-800">
              Matches: {bundleMatches.join(", ")}
            </p>
          )}
        </div>
      )}

      {/* HTML / triple-brace guidance — fields whose value is raw HTML (or an
          <img> tag) must use triple braces in the SendGrid template, or
          SendGrid escapes the HTML and it renders as visible text. */}
      {mergeFields.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span className="font-medium">
            Pasting formatted text, HTML, or images?
          </span>{" "}
          The template slot has to allow it. Plain text is always fine. If a
          value contains HTML (a formatted block or an <code>&lt;img&gt;</code>{" "}
          tag), the SendGrid template must reference it with{" "}
          <span className="font-mono">{"{{{field_name}}}"}</span> (triple
          braces) — plain <span className="font-mono">{"{{field_name}}"}</span>{" "}
          shows the raw tags as text. For a plain image <em>URL</em>, put it
          inside an attribute:{" "}
          <span className="font-mono">{'<img src="{{field_name}}">'}</span>. You
          don't have to get this right from memory —{" "}
          <span className="font-medium">Preview</span> runs a readiness check
          and tells you exactly what to change.
        </div>
      )}

      {/* SendGrid reserved fields (locked, shown first) */}
      {mergeFields.some((f) => isSendGridReservedField(f)) && (
        <div className="space-y-2">
          <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            SendGrid System Fields
          </h5>
          {mergeFields
            .filter((f) => isSendGridReservedField(f))
            .map((field) => {
              const info = getReservedFieldInfo(field);
              return (
                <div
                  key={field}
                  className="bg-gray-100 rounded-lg p-3 border border-gray-200 opacity-80"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-gray-200 text-gray-600">
                        {`{{${field}}}`}
                      </span>
                      <span className="text-xs text-gray-500">
                        {info?.label}
                      </span>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      Auto-handled
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {info?.description}
                  </p>
                </div>
              );
            })}
        </div>
      )}

      {/* User-mappable fields */}
      {mergeFields.some((f) => !isSendGridReservedField(f)) && (
        <div className="space-y-2">
          {mergeFields.some((f) => isSendGridReservedField(f)) && (
            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-2">
              Template Fields
            </h5>
          )}
          <div className="space-y-3">
            {mergeFields
              .filter((f) => !isSendGridReservedField(f))
              .map((field) => {
                const mapping = getMapping(field);
                const sourceType = mapping.sourceType;

                return (
                  <div
                    key={field}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-blue-100 text-blue-800">
                        {`{{${field}}}`}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Source Type Selector */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Source
                        </label>
                        <select
                          value={sourceType}
                          onChange={(e) =>
                            handleSourceTypeChange(
                              field,
                              e.target.value as FieldMapping["sourceType"],
                            )
                          }
                          className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        >
                          {visibleSourceTypes.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Field/Value Selector */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          {sourceType === "custom"
                            ? "Value"
                            : sourceType === "url"
                              ? "URL"
                              : sourceType === "landing_page"
                                ? "URL builder"
                                : sourceType === "lookup"
                                  ? "Lookup"
                                  : sourceType === "content"
                                    ? "Content Item"
                                    : "Field"}
                        </label>
                        {sourceType === "landing_page" && (
                          <p className="text-xs text-gray-600 italic px-2 py-1.5 bg-blue-50 border border-blue-100 rounded">
                            URL is built at send time from the landing-page
                            config below the mapper.
                          </p>
                        )}
                        {sourceType === "lookup" && (
                          <p className="text-xs text-gray-600 italic px-2 py-1.5 bg-blue-50 border border-blue-100 rounded">
                            Pick the lookup, value column, and contact key in
                            the panel below.
                          </p>
                        )}
                        {sourceType === "content" && (
                          <p className="text-xs text-gray-600 italic px-2 py-1.5 bg-blue-50 border border-blue-100 rounded">
                            Pick an IMAGE Content Item in the panel below.
                          </p>
                        )}
                        {sourceType === "contact" && (
                          <select
                            value={mapping.sourceField || ""}
                            onChange={(e) =>
                              handleSourceFieldChange(field, e.target.value)
                            }
                            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select field...</option>
                            {contactFields.map((f) => (
                              <option key={f.value} value={f.value}>
                                {f.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {sourceType === "voucher" && (
                          <select
                            value={mapping.sourceField || ""}
                            onChange={(e) =>
                              handleSourceFieldChange(field, e.target.value)
                            }
                            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select field...</option>
                            {VOUCHER_FIELDS.map((f) => (
                              <option key={f.value} value={f.value}>
                                {f.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {sourceType === "url" && (
                          <input
                            type="url"
                            value={mapping.customValue || ""}
                            onChange={(e) =>
                              handleCustomValueChange(field, e.target.value)
                            }
                            placeholder="https://..."
                            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                        )}
                        {sourceType === "custom" && (
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={mapping.customValue || ""}
                              onChange={(e) =>
                                handleCustomValueChange(field, e.target.value)
                              }
                              placeholder="Enter value..."
                              className="flex-1 text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            />
                            <select
                              value=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleCustomValueChange(
                                    field,
                                    e.target.value,
                                  );
                                }
                              }}
                              className="w-8 text-sm px-1 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-gray-400"
                              title="Quick insert"
                            >
                              <option value="">+</option>
                              {SYSTEM_FIELDS.map((f) => (
                                <option key={f.value} value={`{{${f.value}}}`}>
                                  {f.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mapping status indicator */}
                    {(sourceType === "contact" || sourceType === "voucher") &&
                      !mapping.sourceField && (
                        <p className="mt-1 text-xs text-amber-600">
                          Please select a field to map
                        </p>
                      )}
                    {sourceType === "url" && !mapping.customValue && (
                      <p className="mt-1 text-xs text-amber-600">
                        Enter a URL or this field will be empty
                      </p>
                    )}
                    {sourceType === "custom" && !mapping.customValue && (
                      <p className="mt-1 text-xs text-amber-600">
                        Enter a value or this field will be empty
                      </p>
                    )}

                    {/* Lookup-specific config panel (renders below the
                        2-col grid because it has its own dropdowns + filter
                        builder + fallback input). */}
                    {sourceType === "lookup" && (
                      <LookupSourceEditor
                        mapping={mapping}
                        onChange={(updates) => updateMapping(field, updates)}
                      />
                    )}

                    {/* Content-specific config panel: IMAGE-only
                        typeahead picker + stale-reference warning. */}
                    {sourceType === "content" && (
                      <ContentSourceEditor
                        mapping={mapping}
                        onChange={(updates) => updateMapping(field, updates)}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Additional (user-defined) variables */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Additional Variables
          </h5>
          {!showAddForm && (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-0.5"
            >
              <span className="text-base leading-none">+</span> Add variable
            </button>
          )}
        </div>

        {extraMappings.length === 0 && !showAddForm && (
          <p className="text-xs text-gray-400 italic">
            Add variables that exist in your template but weren't auto-detected,
            or plan ahead for variables you intend to add.
          </p>
        )}

        {extraMappings.map((mapping) => {
          const field = mapping.templateField;
          const sourceType = mapping.sourceType;
          return (
            <div
              key={field}
              className="bg-amber-50 rounded-lg p-3 border border-amber-200"
            >
              <div className="flex items-center gap-2 mb-2 justify-between">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-amber-100 text-amber-800">
                  {`{{${field}}}`}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveExtra(field)}
                  className="text-xs text-gray-400 hover:text-red-500"
                  title="Remove variable"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Source
                  </label>
                  <select
                    value={sourceType}
                    onChange={(e) =>
                      handleSourceTypeChange(
                        field,
                        e.target.value as FieldMapping["sourceType"],
                      )
                    }
                    className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {visibleSourceTypes.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {sourceType === "custom"
                      ? "Value"
                      : sourceType === "url"
                        ? "URL"
                        : sourceType === "lookup"
                          ? "Lookup"
                          : "Field"}
                  </label>
                  {sourceType === "lookup" && (
                    <p className="text-xs text-gray-600 italic px-2 py-1.5 bg-blue-50 border border-blue-100 rounded">
                      Pick the lookup, value column, and contact key in the
                      panel below.
                    </p>
                  )}
                  {sourceType === "contact" && (
                    <select
                      value={mapping.sourceField || ""}
                      onChange={(e) =>
                        handleSourceFieldChange(field, e.target.value)
                      }
                      className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select field...</option>
                      {contactFields.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {sourceType === "voucher" && (
                    <select
                      value={mapping.sourceField || ""}
                      onChange={(e) =>
                        handleSourceFieldChange(field, e.target.value)
                      }
                      className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select field...</option>
                      {VOUCHER_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {(sourceType === "url" || sourceType === "custom") && (
                    <input
                      type={sourceType === "url" ? "url" : "text"}
                      value={mapping.customValue || ""}
                      onChange={(e) =>
                        handleCustomValueChange(field, e.target.value)
                      }
                      placeholder={
                        sourceType === "url" ? "https://..." : "Enter value..."
                      }
                      className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  )}
                </div>
              </div>
              {sourceType === "lookup" && (
                <LookupSourceEditor
                  mapping={mapping}
                  onChange={(updates) => updateMapping(field, updates)}
                />
              )}
            </div>
          );
        })}

        {showAddForm && (
          <div className="bg-gray-50 rounded-lg p-3 border border-dashed border-gray-300">
            <p className="text-xs text-gray-500 mb-2">
              Variable name (letters, numbers, underscores only):
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCustomVariable();
                  }
                  if (e.key === "Escape") {
                    setShowAddForm(false);
                  }
                }}
                placeholder="e.g. promo_code"
                autoFocus
                className="flex-1 text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 font-mono"
              />
              <button
                type="button"
                onClick={handleAddCustomVariable}
                disabled={!newVarName.trim()}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-40"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewVarName("");
                }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Use this to pre-configure variables you plan to add to your
              SendGrid template.
            </p>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-xs text-blue-800">
            <p className="font-medium mb-1">Field Mapping Tips</p>
            <ul className="space-y-0.5 text-blue-700">
              <li>Contact fields pull from each recipient's contact record</li>
              {includeVoucherFields && (
                <li>Voucher fields are generated per recipient at send time</li>
              )}
              <li>
                Link/URL inserts a URL (flipbook, video, landing page, etc.)
              </li>
              <li>Custom values are the same for all recipients</li>
              <li>
                SendGrid system fields are handled automatically at send time
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
